import React from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { Logo } from "@/components/shared/Logo";
import { LanguageSwitcher } from "@/components/shared/LanguageSwitcher";
import { ThemeSwitcher } from "@/components/shared/ThemeSwitcher";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { LayoutDashboard, User, Crown, ShieldCheck, Users, LifeBuoy, LogOut, Home, Send } from "lucide-react";

const items = [
  ["/dashboard", "my_dashboard", LayoutDashboard],
  ["/dashboard/profile", "profile", User],
  ["/dashboard/vip-access", "vip_status", Crown],
  ["/dashboard/broker-verification", "broker_verification", ShieldCheck],
  ["/dashboard/referrals", "referrals", Users],
  ["/dashboard/tickets", "support", LifeBuoy],
];

export function DashboardLayout() {
  const { user, logout } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-brand-navy grid-bg">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-20">
          <Logo size="h-8" />
          <div className="flex items-center gap-3">
            <ThemeSwitcher />
            <LanguageSwitcher />
            <button onClick={() => navigate("/")} className="text-slate-300 hover:text-brand-cyan p-2" data-testid="dash-home-btn"><Home className="h-5 w-5" /></button>
            {user?.role === "admin" && (
              <button onClick={() => navigate("/admin")} className="text-sm text-brand-cyan hover:underline" data-testid="dash-admin-link">{t("nav_admin")}</button>
            )}
          </div>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-6 pb-16 pt-2">
          <aside className="lg:sticky lg:top-6 h-fit">
            <div className="card-surface rounded-2xl p-3">
              <div className="flex items-center gap-3 p-3 mb-2">
                <div className="h-10 w-10 rounded-full gradient-brand flex items-center justify-center text-brand-navy font-bold">
                  {(user?.name || "U").charAt(0).toUpperCase()}
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-white truncate">{user?.name}</p>
                  <p className="text-xs text-slate-500 truncate">{user?.email}</p>
                </div>
              </div>
              <nav className="space-y-1">
                {items.map(([path, key, Icon]) => (
                  <NavLink key={path} to={path} end={path === "/dashboard"}
                    data-testid={`side-${key}`}
                    className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${isActive ? "bg-brand-cyan/10 text-brand-cyan" : "text-slate-300 hover:bg-white/5"}`}>
                    <Icon className="h-4 w-4" /> {t(key)}
                  </NavLink>
                ))}
                <button onClick={logout} data-testid="side-logout"
                  className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-slate-400 hover:bg-red-500/10 hover:text-red-400 transition-colors">
                  <LogOut className="h-4 w-4" /> {t("nav_logout")}
                </button>
              </nav>
            </div>
          </aside>
          <div className="min-w-0"><Outlet /></div>
        </div>
      </div>
    </div>
  );
}
